import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, fmtINR } from "@/lib/api";
import { useProfiles } from "@/lib/profiles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Plus, Trash2, Pencil, TrendingUp, TrendingDown, Wallet, Repeat,
  ArrowDownCircle, ArrowUpCircle, Users, Truck, FolderKanban,
} from "lucide-react";
import { toast } from "sonner";

const fieldCls = "bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-[#D4AF37] text-white font-mont placeholder:text-neutral-600";
const labelCls = "font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500";
const selectCls = "bg-transparent border-0 border-b border-white/20 rounded-none focus:ring-0 text-white font-mont mt-1 px-0";
const menuCls = "bg-[#121212] border-white/10 text-white rounded-none";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const TYPE_LABELS = { income: "Payment from Client", expense: "Payment to Vendor", advance: "Advance Payment" };
const PAYMENT_METHODS = ["Bank Transfer", "UPI", "Cash", "Cheque", "Card", "Other"];

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "transactions", label: "Transactions" },
  { key: "projects", label: "Projects" },
  { key: "vendor_payments", label: "Vendor Payments" },
  { key: "accounts", label: "Accounts" },
  { key: "recurring", label: "Recurring" },
];

const emptyAccount = { name: "", type: "bank", opening_balance: 0 };
const emptyTxn = { type: "income", client_id: "", vendor_id: "", project_id: "", account_id: "", category: "Bank Transfer", amount: "", date: new Date().toISOString().slice(0, 10), description: "", reference: "" };
const emptyRecurring = { account_id: "", name: "", type: "expense", category: "", amount: "", frequency: "monthly", next_due_date: new Date().toISOString().slice(0, 10), is_active: true };
const emptyProject = { name: "", client_id: "", budget: "", status: "active" };

export default function Finance() {
  const { profiles, activeProfileId, setActiveProfileId } = useProfiles();
  const [tab, setTab] = useState("overview");

  const now = new Date();
  const [periodType, setPeriodType] = useState("monthly"); // "monthly" | "yearly"
  const [filterMonth, setFilterMonth] = useState(now.getMonth() + 1);
  const [filterYear, setFilterYear] = useState(now.getFullYear());

  const [dashboard, setDashboard] = useState({ total_income: 0, total_expense: 0, total_advance: 0, net_balance: 0, client_count: 0, vendor_count: 0, project_count: 0, recent_transactions: [], project_breakdown: [] });
  const [summary, setSummary] = useState({ total_income: 0, total_expense: 0, net: 0, accounts: [] });
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [accOpen, setAccOpen] = useState(false);
  const [accForm, setAccForm] = useState(emptyAccount);
  const [txnOpen, setTxnOpen] = useState(false);
  const [txnEditing, setTxnEditing] = useState(null);
  const [txnForm, setTxnForm] = useState(emptyTxn);
  const [recOpen, setRecOpen] = useState(false);
  const [recForm, setRecForm] = useState(emptyRecurring);
  const [projOpen, setProjOpen] = useState(false);
  const [projEditing, setProjEditing] = useState(null);
  const [projForm, setProjForm] = useState(emptyProject);

  const profileId = activeProfileId || profiles[0]?.id;

  const loadAll = useCallback(async () => {
    if (!profileId) return;
    try {
      const dashUrl = periodType === "yearly"
        ? `/finance/dashboard?profile_id=${profileId}&year=${filterYear}`
        : `/finance/dashboard?profile_id=${profileId}&month=${filterMonth}&year=${filterYear}`;
      const [dash, s, a, t, r, p, cl, vn] = await Promise.all([
        api.get(dashUrl),
        api.get(`/finance/summary?profile_id=${profileId}`),
        api.get(`/finance/accounts?profile_id=${profileId}`),
        api.get(`/finance/transactions?profile_id=${profileId}`),
        api.get(`/finance/recurring?profile_id=${profileId}`),
        api.get(`/finance/projects?profile_id=${profileId}`),
        api.get(`/clients?profile_id=${profileId}`),
        api.get(`/vendors?profile_id=${profileId}`),
      ]);
      setDashboard(dash.data);
      setSummary(s.data);
      setAccounts(a.data);
      setTransactions(t.data);
      setRecurring(r.data);
      setProjects(p.data);
      setClients(cl.data);
      setVendors(vn.data);
    } catch {
      toast.error("Failed to load finance data for this business");
    }
  }, [profileId, periodType, filterMonth, filterYear]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const accountName = (id) => accounts.find((a) => a.id === id)?.name || "—";
  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || "—";
  const projectName = (id) => projects.find((p) => p.id === id)?.name || "";

  const partyName = (t) => {
    if (t.client_id) return clientName(t.client_id);
    if (t.vendor_id) return vendorName(t.vendor_id);
    return t.description || "—";
  };

  const years = useMemo(() => {
    const y = new Date().getFullYear();
    return [y - 2, y - 1, y, y + 1];
  }, []);

  // Vendor Payments tab — filters + derived data
  const [vpPeriodType, setVpPeriodType] = useState("monthly"); // "monthly" | "yearly" | "custom"
  const [vpMonth, setVpMonth] = useState(now.getMonth() + 1);
  const [vpYear, setVpYear] = useState(now.getFullYear());
  const [vpFrom, setVpFrom] = useState("");
  const [vpTo, setVpTo] = useState("");
  const [vpProjectId, setVpProjectId] = useState("all");
  const [vpExpanded, setVpExpanded] = useState({});
  const toggleVendorExpand = (id) => setVpExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const vendorPayments = useMemo(() => {
    const inRange = (dateStr) => {
      if (!dateStr) return false;
      if (vpPeriodType === "monthly") {
        const d = new Date(dateStr + "T00:00:00");
        return d.getMonth() + 1 === vpMonth && d.getFullYear() === vpYear;
      }
      if (vpPeriodType === "yearly") {
        const d = new Date(dateStr + "T00:00:00");
        return d.getFullYear() === vpYear;
      }
      // custom range — an empty bound means unbounded on that side
      if (vpFrom && dateStr < vpFrom) return false;
      if (vpTo && dateStr > vpTo) return false;
      return true;
    };

    const filtered = transactions.filter((t) => {
      if (t.type !== "expense" || !t.vendor_id) return false;
      if (vpProjectId !== "all" && t.project_id !== vpProjectId) return false;
      return inRange(t.date);
    });

    const byVendor = {};
    filtered.forEach((t) => {
      if (!byVendor[t.vendor_id]) byVendor[t.vendor_id] = { vendor_id: t.vendor_id, total: 0, count: 0, txns: [] };
      byVendor[t.vendor_id].total += Number(t.amount) || 0;
      byVendor[t.vendor_id].count += 1;
      byVendor[t.vendor_id].txns.push(t);
    });

    const rows = Object.values(byVendor)
      .map((v) => ({ ...v, txns: v.txns.slice().sort((a, b) => (a.date < b.date ? 1 : -1)) }))
      .sort((a, b) => b.total - a.total);

    const grandTotal = rows.reduce((sum, v) => sum + v.total, 0);
    return { rows, grandTotal };
  }, [transactions, vpPeriodType, vpMonth, vpYear, vpFrom, vpTo, vpProjectId]);

  const [isSaving, setIsSaving] = useState(false);

  const saveAccount = async () => {
    if (isSaving) return;
    if (!accForm.name.trim()) { toast.error("Account name required"); return; }
    setIsSaving(true);
    try {
      await api.post(`/finance/accounts?profile_id=${profileId}`, { ...accForm, opening_balance: Number(accForm.opening_balance || 0) });
      toast.success("Account added");
      setAccOpen(false); setAccForm(emptyAccount); loadAll();
    } catch { toast.error("Failed to add account"); } finally { setIsSaving(false); }
  };
  const removeAccount = async (id) => {
    if (!window.confirm("Delete this account? Its transactions will also be removed.")) return;
    await api.delete(`/finance/accounts/${id}`);
    toast.success("Account deleted"); loadAll();
  };

  const openNewTxn = () => { setTxnEditing(null); setTxnForm(emptyTxn); setTxnOpen(true); };
  const openEditTxn = (t) => {
    setTxnEditing(t);
    setTxnForm({
      type: t.type,
      client_id: t.client_id || "",
      vendor_id: t.vendor_id || "",
      project_id: t.project_id || "",
      account_id: t.account_id || "",
      category: t.category || "Bank Transfer",
      amount: t.amount,
      date: t.date,
      description: t.description || "",
      reference: t.reference || "",
    });
    setTxnOpen(true);
  };

  const saveTxn = async () => {
    if (isSaving) return;
    if (txnForm.type === "income" && !txnForm.client_id) { toast.error("Select a client"); return; }
    if (txnForm.type !== "income" && !txnForm.vendor_id) { toast.error("Select a vendor"); return; }
    if (!txnForm.amount || Number(txnForm.amount) <= 0) { toast.error("Enter a valid amount"); return; }
    setIsSaving(true);
    try {
      const payload = { ...txnForm, amount: Number(txnForm.amount) };
      if (txnForm.type === "income") payload.vendor_id = "";
      else payload.client_id = "";
      if (txnEditing) {
        await api.put(`/finance/transactions/${txnEditing.id}`, payload);
        toast.success("Transaction updated");
      } else {
        await api.post(`/finance/transactions?profile_id=${profileId}`, payload);
        toast.success("Transaction recorded");
      }
      setTxnOpen(false); setTxnEditing(null); setTxnForm(emptyTxn); loadAll();
    } catch { toast.error("Failed to save transaction"); } finally { setIsSaving(false); }
  };

  const removeTxn = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    await api.delete(`/finance/transactions/${id}`);
    toast.success("Deleted"); loadAll();
  };

  const saveRecurring = async () => {
    if (isSaving) return;
    if (!recForm.account_id || !recForm.name.trim()) { toast.error("Fill in account and name"); return; }
    if (!recForm.amount || Number(recForm.amount) <= 0) { toast.error("Enter a valid amount"); return; }
    setIsSaving(true);
    try {
      await api.post(`/finance/recurring?profile_id=${profileId}`, { ...recForm, amount: Number(recForm.amount) });
      toast.success("Recurring payment added");
      setRecOpen(false); setRecForm(emptyRecurring); loadAll();
    } catch { toast.error("Failed to add recurring payment"); } finally { setIsSaving(false); }
  };
  const logRecurring = async (id) => {
    try { await api.post(`/finance/recurring/${id}/log`); toast.success("Payment logged"); loadAll(); }
    catch { toast.error("Failed to log payment"); }
  };
  const removeRecurring = async (id) => {
    if (!window.confirm("Delete this recurring payment?")) return;
    await api.delete(`/finance/recurring/${id}`);
    toast.success("Deleted"); loadAll();
  };

  const openNewProject = () => { setProjEditing(null); setProjForm(emptyProject); setProjOpen(true); };
  const openEditProject = (p) => { setProjEditing(p); setProjForm({ name: p.name, client_id: p.client_id || "", budget: p.budget, status: p.status }); setProjOpen(true); };

  const saveProject = async () => {
    if (isSaving) return;
    if (!projForm.name.trim()) { toast.error("Project name required"); return; }
    setIsSaving(true);
    try {
      const payload = { ...projForm, budget: Number(projForm.budget || 0) };
      if (projEditing) await api.put(`/finance/projects/${projEditing.id}`, payload);
      else await api.post(`/finance/projects?profile_id=${profileId}`, payload);
      toast.success(projEditing ? "Project updated" : "Project created");
      setProjOpen(false); loadAll();
    } catch { toast.error("Failed to save project"); } finally { setIsSaving(false); }
  };
  const removeProject = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/finance/projects/${id}`);
    toast.success("Project deleted"); loadAll();
  };

  if (!profileId) {
    return (
      <div className="px-4 sm:px-6 lg:px-10 py-10 text-neutral-500 font-mont text-xs uppercase tracking-[0.3em]">
        No business profile available yet.
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <p className="font-mont text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">— Cash Flow</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">Finance</h1>
        </div>
        {profiles.length > 1 && (
          <Select value={profileId} onValueChange={setActiveProfileId}>
            <SelectTrigger className="bg-transparent border border-white/20 rounded-none text-white font-mont w-full sm:w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={menuCls}>
              {profiles.map((p) => (<SelectItem key={p.id} value={p.id} className="font-mont text-sm">{p.name}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="flex gap-1 mb-8 border-b border-white/10 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`finance-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-3 font-mont text-[10px] uppercase tracking-[0.25em] whitespace-nowrap border-b-2 transition-colors ${
              tab === t.key ? "border-[#D4AF37] text-white" : "border-transparent text-neutral-500 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className={labelCls}>Filter Period</span>
            <div className="flex border border-white/20">
              <button
                onClick={() => setPeriodType("monthly")}
                className={`px-3 h-9 font-mont text-xs uppercase tracking-widest ${periodType === "monthly" ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setPeriodType("yearly")}
                className={`px-3 h-9 font-mont text-xs uppercase tracking-widest border-l border-white/20 ${periodType === "yearly" ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`}
              >
                Yearly
              </button>
            </div>
            {periodType === "monthly" && (
              <Select value={String(filterMonth)} onValueChange={(v) => setFilterMonth(Number(v))}>
                <SelectTrigger className="bg-transparent border border-white/20 rounded-none text-white font-mont w-40 h-9"><SelectValue /></SelectTrigger>
                <SelectContent className={menuCls}>
                  {MONTHS.map((m, i) => (<SelectItem key={m} value={String(i + 1)} className="font-mont text-sm">{m}</SelectItem>))}
                </SelectContent>
              </Select>
            )}
            <Select value={String(filterYear)} onValueChange={(v) => setFilterYear(Number(v))}>
              <SelectTrigger className="bg-transparent border border-white/20 rounded-none text-white font-mont w-28 h-9"><SelectValue /></SelectTrigger>
              <SelectContent className={menuCls}>
                {years.map((y) => (<SelectItem key={y} value={String(y)} className="font-mont text-sm">{y}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
            <div className="bg-[#121212] p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <span className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500">Total Income</span>
                <TrendingUp className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
              </div>
              <div className="font-mont text-2xl sm:text-3xl text-emerald-400">{fmtINR(dashboard.total_income)}</div>
              <div className="font-mont text-[10px] text-neutral-600 mt-2 uppercase tracking-widest">From clients</div>
            </div>
            <div className="bg-[#121212] p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <span className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500">Total Expenses</span>
                <TrendingDown className="w-4 h-4 text-red-400" strokeWidth={1.5} />
              </div>
              <div className="font-mont text-2xl sm:text-3xl text-red-400">{fmtINR(dashboard.total_expense)}</div>
              <div className="font-mont text-[10px] text-neutral-600 mt-2 uppercase tracking-widest">To vendors</div>
            </div>
            <div className="bg-[#121212] p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <span className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500">Advances Paid</span>
                <Wallet className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <div className="font-mont text-2xl sm:text-3xl text-[#D4AF37]">{fmtINR(dashboard.total_advance)}</div>
              <div className="font-mont text-[10px] text-neutral-600 mt-2 uppercase tracking-widest">Advance payments</div>
            </div>
            <div className="bg-[#121212] p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <span className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500">Net Balance</span>
                <Wallet className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
              <div className={`font-mont text-2xl sm:text-3xl ${dashboard.net_balance >= 0 ? "text-white" : "text-red-400"}`}>{fmtINR(dashboard.net_balance)}</div>
              <div className="font-mont text-[10px] text-neutral-600 mt-2 uppercase tracking-widest">Income − Expenses − Advances</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-10">
            <div className="bg-[#121212] p-6 flex items-center gap-4">
              <Users className="w-5 h-5 text-neutral-500" strokeWidth={1.5} />
              <div>
                <div className="font-mont text-[10px] uppercase tracking-widest text-neutral-500">Clients</div>
                <div className="font-mont text-xl text-white">{dashboard.client_count}</div>
              </div>
            </div>
            <div className="bg-[#121212] p-6 flex items-center gap-4">
              <Truck className="w-5 h-5 text-neutral-500" strokeWidth={1.5} />
              <div>
                <div className="font-mont text-[10px] uppercase tracking-widest text-neutral-500">Vendors</div>
                <div className="font-mont text-xl text-white">{dashboard.vendor_count}</div>
              </div>
            </div>
            <div className="bg-[#121212] p-6 flex items-center gap-4">
              <FolderKanban className="w-5 h-5 text-neutral-500" strokeWidth={1.5} />
              <div>
                <div className="font-mont text-[10px] uppercase tracking-widest text-neutral-500">Projects</div>
                <div className="font-mont text-xl text-white">{dashboard.project_count}</div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500">— Project-wise Breakdown ({periodType === "yearly" ? filterYear : `${MONTHS[filterMonth - 1]} ${filterYear}`})</div>
            <button onClick={() => setTab("projects")} className="font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500 hover:text-[#D4AF37]">All projects →</button>
          </div>
          {dashboard.project_breakdown.length === 0 ? (
            <div className="py-10 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600 border border-white/10 mb-10">No project activity in this period</div>
          ) : (
            <div className="border border-white/10 divide-y divide-white/10 mb-10">
              {dashboard.project_breakdown.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-4 py-4 gap-3">
                  <div className="text-white text-sm truncate">{p.name}</div>
                  <div className="flex items-center gap-6 font-mont text-sm flex-shrink-0">
                    <span className="text-emerald-400">+{fmtINR(p.income)}</span>
                    <span className="text-red-400">−{fmtINR(p.expense)}</span>
                    <span className={p.profit >= 0 ? "text-white" : "text-red-400"}>{fmtINR(p.profit)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between mb-4">
            <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500">— Recent Transactions</div>
            <button onClick={() => setTab("transactions")} className="font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500 hover:text-[#D4AF37]">View all →</button>
          </div>
          {dashboard.recent_transactions.length === 0 ? (
            <div className="py-10 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600 border border-white/10">No transactions this period</div>
          ) : (
            <div className="border border-white/10 divide-y divide-white/10">
              {dashboard.recent_transactions.map((t) => (
                <div key={t.id} className="flex items-center justify-between px-4 py-4 gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {t.type === "income" ? (
                      <ArrowUpCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" strokeWidth={1.5} />
                    ) : (
                      <ArrowDownCircle className={`w-5 h-5 flex-shrink-0 ${t.type === "advance" ? "text-[#D4AF37]" : "text-red-400"}`} strokeWidth={1.5} />
                    )}
                    <div className="min-w-0">
                      <div className="text-white text-sm truncate">{partyName(t)}</div>
                      <div className="font-mont text-[10px] text-neutral-500 truncate">
                        {TYPE_LABELS[t.type]}{projectName(t.project_id) && ` · ${projectName(t.project_id)}`}
                      </div>
                      {t.created_by_name && (
                        <div className="font-mont text-[9px] text-neutral-600 truncate mt-0.5">by {t.created_by_name}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className={`font-mont text-sm ${t.type === "income" ? "text-emerald-400" : "text-red-400"}`}>
                      {t.type === "income" ? "+" : "−"} {fmtINR(t.amount)}
                    </div>
                    <div className="font-mont text-[10px] text-neutral-600">{t.date}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "transactions" && (
        <div>
          <div className="flex justify-end mb-4">
            <Dialog open={txnOpen} onOpenChange={setTxnOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-transaction-btn" onClick={openNewTxn} className="rounded-none bg-white text-black hover:bg-neutral-200">
                  <Plus className="w-4 h-4 mr-2" strokeWidth={2} /> Add Transaction
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121212] border-white/10 rounded-none text-white max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-serif text-2xl">{txnEditing ? "Edit Transaction" : "New Transaction"}</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className={labelCls}>Type</Label>
                    <Select value={txnForm.type} onValueChange={(v) => setTxnForm({ ...txnForm, type: v })}>
                      <SelectTrigger className={selectCls}><SelectValue /></SelectTrigger>
                      <SelectContent className={menuCls}>
                        <SelectItem value="income" className="font-mont text-sm">Payment from Client</SelectItem>
                        <SelectItem value="expense" className="font-mont text-sm">Payment to Vendor</SelectItem>
                        <SelectItem value="advance" className="font-mont text-sm">Advance Payment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {txnForm.type === "income" ? (
                    <div>
                      <Label className={labelCls}>Client</Label>
                      <Select value={txnForm.client_id} onValueChange={(v) => setTxnForm({ ...txnForm, client_id: v })}>
                        <SelectTrigger className={selectCls}><SelectValue placeholder="Select client" /></SelectTrigger>
                        <SelectContent className={menuCls}>
                          {clients.map((c) => (<SelectItem key={c.id} value={c.id} className="font-mont text-sm">{c.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div>
                      <Label className={labelCls}>Vendor</Label>
                      <Select value={txnForm.vendor_id} onValueChange={(v) => setTxnForm({ ...txnForm, vendor_id: v })}>
                        <SelectTrigger className={selectCls}><SelectValue placeholder="Select vendor" /></SelectTrigger>
                        <SelectContent className={menuCls}>
                          {vendors.map((v) => (<SelectItem key={v.id} value={v.id} className="font-mont text-sm">{v.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <Label className={labelCls}>Project (optional)</Label>
                    <Select value={txnForm.project_id || "none"} onValueChange={(v) => setTxnForm({ ...txnForm, project_id: v === "none" ? "" : v })}>
                      <SelectTrigger className={selectCls}><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent className={menuCls}>
                        <SelectItem value="none" className="font-mont text-sm">— None —</SelectItem>
                        {projects.map((p) => (<SelectItem key={p.id} value={p.id} className="font-mont text-sm">{p.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className={labelCls}>Amount</Label>
                    <Input type="number" step="any" value={txnForm.amount} onChange={(e) => setTxnForm({ ...txnForm, amount: e.target.value })} className={fieldCls} />
                  </div>

                  <div>
                    <Label className={labelCls}>Payment Method</Label>
                    <Select value={txnForm.category} onValueChange={(v) => setTxnForm({ ...txnForm, category: v })}>
                      <SelectTrigger className={selectCls}><SelectValue /></SelectTrigger>
                      <SelectContent className={menuCls}>
                        {PAYMENT_METHODS.map((m) => (<SelectItem key={m} value={m} className="font-mont text-sm">{m}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className={labelCls}>Date</Label>
                    <Input type="date" value={txnForm.date} onChange={(e) => setTxnForm({ ...txnForm, date: e.target.value })} className={fieldCls + " [color-scheme:dark]"} />
                  </div>

                  <div>
                    <Label className={labelCls}>Reference / Invoice #</Label>
                    <Input value={txnForm.reference} onChange={(e) => setTxnForm({ ...txnForm, reference: e.target.value })} placeholder="Optional" className={fieldCls} />
                  </div>

                  <div>
                    <Label className={labelCls}>Description</Label>
                    <Input value={txnForm.description} onChange={(e) => setTxnForm({ ...txnForm, description: e.target.value })} placeholder="Transaction details…" className={fieldCls} />
                  </div>

                  {accounts.length > 0 && (
                    <div>
                      <Label className={labelCls}>Account (optional)</Label>
                      <Select value={txnForm.account_id || "none"} onValueChange={(v) => setTxnForm({ ...txnForm, account_id: v === "none" ? "" : v })}>
                        <SelectTrigger className={selectCls}><SelectValue placeholder="Not linked to an account" /></SelectTrigger>
                        <SelectContent className={menuCls}>
                          <SelectItem value="none" className="font-mont text-sm">— None —</SelectItem>
                          {accounts.map((a) => (<SelectItem key={a.id} value={a.id} className="font-mont text-sm">{a.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <Button data-testid="save-transaction-btn" disabled={isSaving} onClick={saveTxn} className="w-full rounded-none bg-white text-black hover:bg-neutral-200 disabled:opacity-50">{isSaving ? "Saving…" : (txnEditing ? "Save Changes" : "Add Transaction")}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="border border-white/10 divide-y divide-white/10">
            {transactions.length === 0 ? (
              <div className="py-20 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600">No transactions yet</div>
            ) : (
              transactions.map((t) => (
                <div key={t.id} data-testid={`txn-row-${t.id}`} className="flex items-center justify-between px-4 py-4 gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {t.type === "income" ? (
                      <ArrowUpCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" strokeWidth={1.5} />
                    ) : (
                      <ArrowDownCircle className={`w-5 h-5 flex-shrink-0 ${t.type === "advance" ? "text-[#D4AF37]" : "text-red-400"}`} strokeWidth={1.5} />
                    )}
                    <div className="min-w-0">
                      <div className="text-white text-sm truncate">{partyName(t)}</div>
                      <div className="font-mont text-[10px] text-neutral-500 truncate">
                        {TYPE_LABELS[t.type]}{projectName(t.project_id) && ` · ${projectName(t.project_id)}`} · {t.date}
                        {t.account_id && ` · ${accountName(t.account_id)}`}
                      </div>
                      {(t.created_by_name || t.updated_by_name) && (
                        <div className="font-mont text-[9px] text-neutral-600 truncate mt-0.5">
                          {t.updated_by_name && t.updated_by_name !== t.created_by_name
                            ? `Added by ${t.created_by_name} · Last edited by ${t.updated_by_name}`
                            : `Added by ${t.created_by_name || t.updated_by_name}`}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`font-mont text-sm ${t.type === "income" ? "text-emerald-400" : "text-red-400"}`}>
                      {t.type === "income" ? "+" : "−"} {fmtINR(t.amount)}
                    </span>
                    <button onClick={() => openEditTxn(t)} className="text-neutral-600 hover:text-white"><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
                    <button onClick={() => removeTxn(t.id)} className="text-neutral-600 hover:text-red-400"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {tab === "projects" && (
        <div>
          <div className="flex justify-end mb-4">
            <Dialog open={projOpen} onOpenChange={setProjOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-project-btn" onClick={openNewProject} className="rounded-none bg-white text-black hover:bg-neutral-200">
                  <Plus className="w-4 h-4 mr-2" strokeWidth={2} /> Add Project
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121212] border-white/10 rounded-none text-white max-w-lg">
                <DialogHeader><DialogTitle className="font-serif text-2xl">{projEditing ? "Edit Project" : "Add Project"}</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className={labelCls}>Project Name</Label>
                    <Input value={projForm.name} onChange={(e) => setProjForm({ ...projForm, name: e.target.value })} className={fieldCls} />
                  </div>
                  <div>
                    <Label className={labelCls}>Client</Label>
                    <Select value={projForm.client_id || "none"} onValueChange={(v) => setProjForm({ ...projForm, client_id: v === "none" ? "" : v })}>
                      <SelectTrigger className={selectCls}><SelectValue placeholder="Select client" /></SelectTrigger>
                      <SelectContent className={menuCls}>
                        <SelectItem value="none" className="font-mont text-sm">— None —</SelectItem>
                        {clients.map((c) => (<SelectItem key={c.id} value={c.id} className="font-mont text-sm">{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={labelCls}>Budget</Label>
                    <Input type="number" step="any" value={projForm.budget} onChange={(e) => setProjForm({ ...projForm, budget: e.target.value })} className={fieldCls} />
                  </div>
                  <div>
                    <Label className={labelCls}>Status</Label>
                    <Select value={projForm.status} onValueChange={(v) => setProjForm({ ...projForm, status: v })}>
                      <SelectTrigger className={selectCls}><SelectValue /></SelectTrigger>
                      <SelectContent className={menuCls}>
                        <SelectItem value="active" className="font-mont text-sm">Active</SelectItem>
                        <SelectItem value="on_hold" className="font-mont text-sm">On Hold</SelectItem>
                        <SelectItem value="completed" className="font-mont text-sm">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="save-project-btn" disabled={isSaving} onClick={saveProject} className="w-full rounded-none bg-white text-black hover:bg-neutral-200 disabled:opacity-50">{isSaving ? "Saving…" : "Save Project"}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {projects.length === 0 ? (
            <div className="py-20 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600 border border-white/10">No projects yet</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {projects.map((p) => (
                <div key={p.id} data-testid={`project-card-${p.name}`} className="bg-[#121212] border border-white/10 p-6">
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-serif text-lg">{p.name}</span>
                      <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
                        p.status === "active" ? "text-emerald-400 border-emerald-400/40" :
                        p.status === "completed" ? "text-neutral-400 border-neutral-500/40" :
                        "text-[#D4AF37] border-[#D4AF37]/40"
                      }`}>{p.status.replace("_", " ")}</span>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => openEditProject(p)} className="text-neutral-500 hover:text-white"><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
                      <button onClick={() => removeProject(p.id)} className="text-neutral-500 hover:text-red-400"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                    </div>
                  </div>
                  {p.client_id && <div className="font-mont text-xs text-neutral-500 mb-1">Client: {clientName(p.client_id)}</div>}
                  <div className="font-mont text-xs text-neutral-500 mb-4">Budget: {fmtINR(p.budget)}</div>
                  <div className="grid grid-cols-3 gap-3 pt-4 border-t border-white/10 font-mont text-sm">
                    <div>
                      <div className="text-[9px] uppercase text-neutral-600 mb-1">Income</div>
                      <div className="text-emerald-400">{fmtINR(p.income)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-neutral-600 mb-1">Expenses</div>
                      <div className="text-red-400">{fmtINR(p.expenses)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-neutral-600 mb-1">Profit</div>
                      <div className={p.profit >= 0 ? "text-white" : "text-red-400"}>{fmtINR(p.profit)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "vendor_payments" && (
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className={labelCls}>Filter Period</span>
            <div className="flex border border-white/20">
              <button
                onClick={() => setVpPeriodType("monthly")}
                className={`px-3 h-9 font-mont text-xs uppercase tracking-widest ${vpPeriodType === "monthly" ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setVpPeriodType("yearly")}
                className={`px-3 h-9 font-mont text-xs uppercase tracking-widest border-l border-white/20 ${vpPeriodType === "yearly" ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`}
              >
                Yearly
              </button>
              <button
                onClick={() => setVpPeriodType("custom")}
                className={`px-3 h-9 font-mont text-xs uppercase tracking-widest border-l border-white/20 ${vpPeriodType === "custom" ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`}
              >
                Custom Range
              </button>
            </div>

            {vpPeriodType === "monthly" && (
              <Select value={String(vpMonth)} onValueChange={(v) => setVpMonth(Number(v))}>
                <SelectTrigger className="bg-transparent border border-white/20 rounded-none text-white font-mont w-40 h-9"><SelectValue /></SelectTrigger>
                <SelectContent className={menuCls}>
                  {MONTHS.map((m, i) => (<SelectItem key={m} value={String(i + 1)} className="font-mont text-sm">{m}</SelectItem>))}
                </SelectContent>
              </Select>
            )}
            {(vpPeriodType === "monthly" || vpPeriodType === "yearly") && (
              <Select value={String(vpYear)} onValueChange={(v) => setVpYear(Number(v))}>
                <SelectTrigger className="bg-transparent border border-white/20 rounded-none text-white font-mont w-28 h-9"><SelectValue /></SelectTrigger>
                <SelectContent className={menuCls}>
                  {years.map((y) => (<SelectItem key={y} value={String(y)} className="font-mont text-sm">{y}</SelectItem>))}
                </SelectContent>
              </Select>
            )}
            {vpPeriodType === "custom" && (
              <>
                <Input type="date" value={vpFrom} onChange={(e) => setVpFrom(e.target.value)} className={fieldCls + " [color-scheme:dark] w-36"} />
                <span className="text-neutral-600 font-mont text-xs">to</span>
                <Input type="date" value={vpTo} onChange={(e) => setVpTo(e.target.value)} className={fieldCls + " [color-scheme:dark] w-36"} />
              </>
            )}

            <Select value={vpProjectId} onValueChange={setVpProjectId}>
              <SelectTrigger className="bg-transparent border border-white/20 rounded-none text-white font-mont w-48 h-9"><SelectValue placeholder="All Projects" /></SelectTrigger>
              <SelectContent className={menuCls}>
                <SelectItem value="all" className="font-mont text-sm">All Projects</SelectItem>
                {projects.map((p) => (<SelectItem key={p.id} value={p.id} className="font-mont text-sm">{p.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10 mb-8">
            <div className="bg-[#121212] p-6 sm:p-8">
              <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Total Paid to Vendors</div>
              <div className="font-mont text-2xl sm:text-3xl text-red-400">{fmtINR(vendorPayments.grandTotal)}</div>
            </div>
            <div className="bg-[#121212] p-6 sm:p-8">
              <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Vendors Paid</div>
              <div className="font-mont text-2xl sm:text-3xl text-white">{vendorPayments.rows.length}</div>
            </div>
          </div>

          {vendorPayments.rows.length === 0 ? (
            <div className="py-20 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600 border border-white/10">No vendor payments in this period</div>
          ) : (
            <div className="border border-white/10 divide-y divide-white/10">
              {vendorPayments.rows.map((v) => (
                <div key={v.vendor_id}>
                  <button
                    onClick={() => toggleVendorExpand(v.vendor_id)}
                    data-testid={`vendor-payment-row-${v.vendor_id}`}
                    className="w-full flex items-center justify-between px-4 py-4 gap-3 hover:bg-white/[0.03] text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Truck className="w-5 h-5 text-neutral-500 flex-shrink-0" strokeWidth={1.5} />
                      <div className="min-w-0">
                        <div className="text-white text-sm truncate">{vendorName(v.vendor_id)}</div>
                        <div className="font-mont text-[10px] text-neutral-500">{v.count} payment{v.count !== 1 ? "s" : ""}</div>
                      </div>
                    </div>
                    <div className="font-mont text-sm text-red-400 flex-shrink-0">{fmtINR(v.total)}</div>
                  </button>
                  {vpExpanded[v.vendor_id] && (
                    <div className="bg-white/[0.02] divide-y divide-white/5">
                      {v.txns.map((t) => (
                        <div key={t.id} className="flex items-center justify-between px-4 py-3 pl-12 gap-3">
                          <div className="min-w-0">
                            <div className="font-mont text-xs text-neutral-400">
                              {t.date}
                              {(t.created_by_name || t.updated_by_name) && (
                                <span className="text-neutral-600">
                                  {" · by "}
                                  {t.updated_by_name && t.updated_by_name !== t.created_by_name
                                    ? `${t.created_by_name} (edited by ${t.updated_by_name})`
                                    : (t.created_by_name || t.updated_by_name)}
                                </span>
                              )}
                              {projectName(t.project_id) && ` · ${projectName(t.project_id)}`}
                            </div>
                            {t.description && <div className="font-mont text-[10px] text-neutral-600 truncate">{t.description}</div>}
                          </div>
                          <div className="font-mont text-xs text-red-400 flex-shrink-0">{fmtINR(t.amount)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "accounts" && (
        <div>
          <div className="flex justify-end mb-4">
            <Dialog open={accOpen} onOpenChange={setAccOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-account-btn" onClick={() => setAccForm(emptyAccount)} className="rounded-none bg-white text-black hover:bg-neutral-200">
                  <Plus className="w-4 h-4 mr-2" strokeWidth={2} /> Add Account
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121212] border-white/10 rounded-none text-white max-w-lg">
                <DialogHeader><DialogTitle className="font-serif text-2xl">Add Account</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className={labelCls}>Name</Label>
                    <Input value={accForm.name} onChange={(e) => setAccForm({ ...accForm, name: e.target.value })} placeholder="e.g. HDFC Current Account" className={fieldCls} />
                  </div>
                  <div>
                    <Label className={labelCls}>Type</Label>
                    <Select value={accForm.type} onValueChange={(v) => setAccForm({ ...accForm, type: v })}>
                      <SelectTrigger className={selectCls}><SelectValue /></SelectTrigger>
                      <SelectContent className={menuCls}>
                        <SelectItem value="bank" className="font-mont text-sm">Bank</SelectItem>
                        <SelectItem value="cash" className="font-mont text-sm">Cash</SelectItem>
                        <SelectItem value="other" className="font-mont text-sm">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={labelCls}>Opening Balance</Label>
                    <Input type="number" step="any" value={accForm.opening_balance} onChange={(e) => setAccForm({ ...accForm, opening_balance: e.target.value })} className={fieldCls} />
                  </div>
                  <Button data-testid="save-account-btn" disabled={isSaving} onClick={saveAccount} className="w-full rounded-none bg-white text-black hover:bg-neutral-200 disabled:opacity-50">{isSaving ? "Saving…" : "Save Account"}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">— Account Balances</div>
          {summary.accounts.length === 0 ? (
            <div className="py-10 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600 border border-white/10 mb-8">No accounts yet</div>
          ) : (
            <div className="border border-white/10 divide-y divide-white/10 mb-8">
              {summary.accounts.map((a) => (
                <div key={a.id} className="flex items-center justify-between px-4 py-4">
                  <div>
                    <div className="text-white text-sm">{a.name}</div>
                    <div className="font-mont text-[10px] uppercase tracking-widest text-neutral-500">{a.type}</div>
                  </div>
                  <div className={`font-mont text-lg ${a.balance >= 0 ? "text-white" : "text-red-400"}`}>{fmtINR(a.balance)}</div>
                </div>
              ))}
            </div>
          )}

          <div className="border border-white/10 divide-y divide-white/10">
            {accounts.length === 0 ? (
              <div className="py-10 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600">No accounts yet</div>
            ) : (
              accounts.map((a) => (
                <div key={a.id} className="flex items-center justify-between px-4 py-4">
                  <div>
                    <div className="text-white text-sm">{a.name}</div>
                    <div className="font-mont text-[10px] uppercase tracking-widest text-neutral-500">{a.type} · Opening {fmtINR(a.opening_balance)}</div>
                  </div>
                  <button onClick={() => removeAccount(a.id)} className="text-neutral-600 hover:text-red-400"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {tab === "recurring" && (
        <div>
          <div className="flex justify-end mb-4">
            <Dialog open={recOpen} onOpenChange={setRecOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-recurring-btn" onClick={() => setRecForm(emptyRecurring)} className="rounded-none bg-white text-black hover:bg-neutral-200">
                  <Plus className="w-4 h-4 mr-2" strokeWidth={2} /> Add Recurring Payment
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121212] border-white/10 rounded-none text-white max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-serif text-2xl">Add Recurring Payment</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className={labelCls}>Name</Label>
                    <Input value={recForm.name} onChange={(e) => setRecForm({ ...recForm, name: e.target.value })} placeholder="e.g. Office Rent, Salaries" className={fieldCls} />
                  </div>
                  <div>
                    <Label className={labelCls}>Type</Label>
                    <Select value={recForm.type} onValueChange={(v) => setRecForm({ ...recForm, type: v })}>
                      <SelectTrigger className={selectCls}><SelectValue /></SelectTrigger>
                      <SelectContent className={menuCls}>
                        <SelectItem value="income" className="font-mont text-sm">Income</SelectItem>
                        <SelectItem value="expense" className="font-mont text-sm">Expense</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={labelCls}>Account</Label>
                    <Select value={recForm.account_id} onValueChange={(v) => setRecForm({ ...recForm, account_id: v })}>
                      <SelectTrigger className={selectCls}><SelectValue placeholder="Select account" /></SelectTrigger>
                      <SelectContent className={menuCls}>
                        {accounts.map((a) => (<SelectItem key={a.id} value={a.id} className="font-mont text-sm">{a.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={labelCls}>Amount</Label>
                    <Input type="number" step="any" value={recForm.amount} onChange={(e) => setRecForm({ ...recForm, amount: e.target.value })} className={fieldCls} />
                  </div>
                  <div>
                    <Label className={labelCls}>Frequency</Label>
                    <Select value={recForm.frequency} onValueChange={(v) => setRecForm({ ...recForm, frequency: v })}>
                      <SelectTrigger className={selectCls}><SelectValue /></SelectTrigger>
                      <SelectContent className={menuCls}>
                        <SelectItem value="weekly" className="font-mont text-sm">Weekly</SelectItem>
                        <SelectItem value="monthly" className="font-mont text-sm">Monthly</SelectItem>
                        <SelectItem value="yearly" className="font-mont text-sm">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={labelCls}>Next Due Date</Label>
                    <Input type="date" value={recForm.next_due_date} onChange={(e) => setRecForm({ ...recForm, next_due_date: e.target.value })} className={fieldCls + " [color-scheme:dark]"} />
                  </div>
                  <Button data-testid="save-recurring-btn" disabled={isSaving} onClick={saveRecurring} className="w-full rounded-none bg-white text-black hover:bg-neutral-200 disabled:opacity-50">{isSaving ? "Saving…" : "Save Recurring Payment"}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="border border-white/10 divide-y divide-white/10">
            {recurring.length === 0 ? (
              <div className="py-20 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600">No recurring payments set up</div>
            ) : (
              recurring.map((r) => (
                <div key={r.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <Repeat className="w-4 h-4 text-neutral-500 flex-shrink-0" strokeWidth={1.5} />
                    <div className="min-w-0">
                      <div className="text-white text-sm">{r.name}</div>
                      <div className="font-mont text-[10px] uppercase tracking-widest text-neutral-500">
                        {r.frequency} · Next: {r.next_due_date} · {accountName(r.account_id)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`font-mont text-sm ${r.type === "income" ? "text-emerald-400" : "text-red-400"}`}>
                      {r.type === "income" ? "+" : "−"} {fmtINR(r.amount)}
                    </span>
                    <Button onClick={() => logRecurring(r.id)} className="rounded-none bg-white/10 hover:bg-white/20 text-white text-xs h-8 px-3">Log Payment</Button>
                    <button onClick={() => removeRecurring(r.id)} className="text-neutral-600 hover:text-red-400"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
